import { Controller } from 'stimulus'

export default class extends Controller {
  static targets = ['input', 'hidden', 'results', 'clearButton']

  connect() {
    this.selectedIndex = -1
    this.items = []
    this.debounceTimer = null
    this.abortController = null
  }

  disconnect() {
    this.cancelDebounce()
    this.abortFetch()
  }

  search() {
    if (this.hiddenTarget.value) {
      this.hiddenTarget.value = ''
      this.toggleClearButton(false)
    }

    this.cancelDebounce()
    this.debounceTimer = setTimeout(() => this.performSearch(), 300)
  }

  async performSearch() {
    const query = this.inputTarget.value.trim()

    if (query.length < 2) {
      this.items = []
      this.hideResults()
      return
    }

    this.showLoading()

    try {
      this.abortFetch()
      this.abortController = new AbortController()

      const url = new URL(this.searchUrl, window.location.origin)
      url.searchParams.set('q', query)

      const response = await fetch(url, {
        signal: this.abortController.signal,
        headers: { Accept: 'application/json' }
      })

      if (!response.ok) throw new Error('Search failed')

      this.items = await response.json()
      this.selectedIndex = -1
      this.renderResults()
    } catch (error) {
      if (error.name !== 'AbortError') {
        this.renderError()
      }
    }
  }

  renderResults() {
    if (this.items.length === 0) {
      this.resultsTarget.innerHTML = '<li class="film-typeahead__empty">Ничего не найдено</li>'
    } else {
      this.resultsTarget.innerHTML = this.items.map((item, index) => {
        const activeClass = index === this.selectedIndex ? ' film-typeahead__item--active' : ''
        return `<li class="film-typeahead__item${activeClass}"
          data-action="mousedown->admin--film-typeahead#selectItem"
          data-index="${index}">
          <div class="film-typeahead__item-name">${this.escapeHtml(item.name_ru)}</div>
          <div class="film-typeahead__item-meta">${this.escapeHtml(this.formatMeta(item))}</div>
        </li>`
      }).join('')
    }

    this.showResults()
  }

  formatMeta(item) {
    const parts = []

    if (item.year) parts.push(`Год: ${item.year}`)
    if (item.theatre) parts.push(`Театр: ${item.theatre.name_ru}`)
    parts.push(`ID: ${item.id}`)

    return parts.join(' · ')
  }

  selectItem(event) {
    event.preventDefault()

    const item = this.items[parseInt(event.currentTarget.dataset.index, 10)]
    if (item) this.select(item)
  }

  select(item) {
    this.hiddenTarget.value = item.id
    this.inputTarget.value = item.name_ru
    this.hideResults()
    this.toggleClearButton(true)
  }

  clear(event) {
    event.preventDefault()

    this.hiddenTarget.value = ''
    this.inputTarget.value = ''
    this.items = []
    this.hideResults()
    this.toggleClearButton(false)
    this.inputTarget.focus()
  }

  navigate(event) {
    if (!this.isResultsVisible() || this.items.length === 0) return

    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault()
        this.selectedIndex = Math.min(this.selectedIndex + 1, this.items.length - 1)
        this.renderResults()
        break
      case 'ArrowUp':
        event.preventDefault()
        this.selectedIndex = Math.max(this.selectedIndex - 1, 0)
        this.renderResults()
        break
      case 'Enter':
        event.preventDefault()
        if (this.selectedIndex >= 0 && this.items[this.selectedIndex]) {
          this.select(this.items[this.selectedIndex])
        }
        break
      case 'Escape':
        this.hideResults()
        break
    }
  }

  showOnFocus() {
    if (this.inputTarget.value.trim().length >= 2 && this.items.length > 0) {
      this.showResults()
    }
  }

  hideResultsDelayed() {
    setTimeout(() => this.hideResults(), 200)
  }

  showResults() {
    this.resultsTarget.hidden = false
    this.element.classList.add('film-typeahead--open')
  }

  hideResults() {
    this.resultsTarget.hidden = true
    this.element.classList.remove('film-typeahead--open')
  }

  showLoading() {
    this.resultsTarget.innerHTML = '<li class="film-typeahead__empty">Поиск…</li>'
    this.showResults()
  }

  renderError() {
    this.resultsTarget.innerHTML = '<li class="film-typeahead__empty">Ошибка поиска</li>'
    this.showResults()
  }

  toggleClearButton(visible) {
    if (this.hasClearButtonTarget) {
      this.clearButtonTarget.hidden = !visible
    }
  }

  isResultsVisible() {
    return !this.resultsTarget.hidden
  }

  get searchUrl() {
    return this.data.get('searchUrl') || '/admin/titles/search'
  }

  cancelDebounce() {
    if (this.debounceTimer) {
      clearTimeout(this.debounceTimer)
      this.debounceTimer = null
    }
  }

  abortFetch() {
    if (this.abortController) {
      this.abortController.abort()
      this.abortController = null
    }
  }

  escapeHtml(text) {
    const div = document.createElement('div')
    div.textContent = text
    return div.innerHTML
  }
}
